import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { membersListService } from '../services/membersList.service';
import type { Member, MemberFilters, MemberStats, RoleDistribution } from '../types/membersList.types';

const initialFilters: MemberFilters = {
  query: '',
  role: 'all',
  status: 'all',
};

export function useMembersList() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const [filters, setFilters] = useState<MemberFilters>(initialFilters);

  const membersQuery = useQuery({
    queryKey: ['settings1', 'members', 'list', companyId],
    queryFn: () => membersListService.getMembers(companyId),
    enabled: !!companyId,
  });

  const invitationsQuery = useQuery({
    queryKey: ['settings1', 'members', 'invitations', companyId],
    queryFn: () => membersListService.getRecentInvitations(companyId),
    enabled: !!companyId,
  });

  const members = membersQuery.data || [];
  const invitations = invitationsQuery.data || [];

  const filteredMembers = useMemo(() => {
    const search = filters.query.toLowerCase().trim();
    return members.filter(m => {
      const matchSearch = !search || [m.fullName, m.email, m.phone, m.roleName]
        .some(v => v.toLowerCase().includes(search));
      const matchStatus = filters.status === 'all' || m.status === filters.status;
      return matchSearch && matchStatus;
    });
  }, [members, filters]);

  const stats = useMemo<MemberStats>(() => ({
    total: members.length,
    active: members.filter(m => m.status === 'active').length,
    locked: members.filter(m => m.status === 'locked').length,
    disabled: members.filter(m => m.status === 'disabled').length,
  }), [members]);

  const distribution = useMemo<RoleDistribution[]>(() => {
    const counts: Record<string, number> = {};
    members.forEach(m => {
      counts[m.roleName] = (counts[m.roleName] || 0) + 1;
    });

    const colors = ['bg-emerald-600', 'bg-blue-600', 'bg-purple-600', 'bg-amber-600', 'bg-slate-400'];
    return Object.entries(counts)
      .map(([name, count], i) => ({
        name,
        count,
        percentage: members.length > 0 ? Math.round((count / members.length) * 100) : 0,
        color: colors[i % colors.length]
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [members]);

  return {
    members: filteredMembers,
    allMembers: members,
    invitations,
    stats,
    distribution,
    filters,
    isLoading: membersQuery.isLoading || invitationsQuery.isLoading,
    setFilters,
    currentUserId: profile?.id,
  };
}
